import React, { useState } from "react";
import { doc, setDoc, getDoc, Timestamp } from "firebase/firestore";
import { db } from "../../firebase";

interface CreatePlanProps {
  onClose: () => void;
}

type ItemType = "treatment" | "back_assessment" | "arm_assessment" | "knee_assessment";

type PlanItem = {
  type: ItemType;
  completed: boolean; // always saved as false in template
};

type PlanDay = {
  items: PlanItem[];
};

const ITEM_OPTIONS: { label: string; value: ItemType }[] = [
  { label: "Treatment", value: "treatment" },
  { label: "Back Assessment", value: "back_assessment" },
  { label: "Arm Assessment", value: "arm_assessment" },
  { label: "Knee Assessment", value: "knee_assessment" },
];

const CreatePlan: React.FC<CreatePlanProps> = ({ onClose }) => {
  const [planName, setPlanName] = useState("");
  const [days, setDays] = useState<PlanDay[]>([]);
  const [checkingName, setCheckingName] = useState(false);
  const [nameError, setNameError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // --- Helpers ---
  const addDay = () => {
    setDays((prev) => [...prev, { items: [] }]);
  };

  const removeDay = (index: number) => {
    setDays((prev) => prev.filter((_, i) => i !== index));
  };

  const moveDay = (index: number, dir: -1 | 1) => {
    setDays((prev) => {
      const next = [...prev];
      const newIndex = index + dir;
      if (newIndex < 0 || newIndex >= next.length) return prev;
      const [d] = next.splice(index, 1);
      next.splice(newIndex, 0, d);
      return next;
    });
  };

  const addItem = (dayIndex: number, type: ItemType = "treatment") => {
    setDays((prev) => {
      const next = [...prev];
      next[dayIndex] = {
        ...next[dayIndex],
        items: [...next[dayIndex].items, { type, completed: false }],
      };
      return next;
    });
  };

  const removeItem = (dayIndex: number, itemIndex: number) => {
    setDays((prev) => {
      const next = [...prev];
      next[dayIndex] = {
        ...next[dayIndex],
        items: next[dayIndex].items.filter((_, i) => i !== itemIndex),
      };
      return next;
    });
  };

  const changeItemType = (dayIndex: number, itemIndex: number, type: ItemType) => {
    setDays((prev) => {
      const next = [...prev];
      next[dayIndex] = {
        ...next[dayIndex],
        items: next[dayIndex].items.map((it, i) =>
          i === itemIndex ? { ...it, type } : it
        ),
      };
      return next;
    });
  };

  const clearAll = () => {
    setPlanName("");
    setDays([]);
    setError(null);
    setStatus(null);
    setNameError(null);
  };

  // --- Validation & uniqueness check ---
  const checkUniqueName = async () => {
    setNameError(null);
    setCheckingName(true);
    try {
      const trimmed = planName.trim();
      if (!trimmed) {
        setNameError("Plan name is required.");
        return false;
      }
      const ref = doc(db, "recovery_plans", trimmed);
      const exists = await getDoc(ref);
      if (exists.exists()) {
        setNameError("A plan with this name already exists. Choose another.");
        return false;
      }
      return true;
    } catch (e: any) {
      setNameError(e?.message ?? "Failed to verify name uniqueness.");
      return false;
    } finally {
      setCheckingName(false);
    }
  };

  // --- Save ---
  const savePlan = async () => {
    setStatus(null);
    setError(null);

    const isUnique = await checkUniqueName();
    if (!isUnique) return;

    // Basic client-side validation
    if (days.length === 0) {
      setError("Add at least one day to the plan.");
      return;
    }

    const trimmed = planName.trim();

    // Build payload to match your template (completed stays false)
    const payload = {
      name: trimmed,
      days: days.map((d) => ({
        items: d.items.map((it) => ({
          type: it.type,
          completed: false,
        })),
      })),
      createdAt: Timestamp.now(),
    };

    setSaving(true);
    try {
      const ref = doc(db, "recovery_plans", trimmed);
      // Since we enforce uniqueness beforehand, use setDoc without merge
      await setDoc(ref, payload);
      setStatus("Plan saved!");
    } catch (e: any) {
      setError(e?.message ?? "Failed to save plan.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50">
      <div className="relative bg-white p-6 md:p-8 rounded-3xl shadow-2xl w-11/12 lg:w-4/5 max-h-[90vh] overflow-auto">
        <h2 className="text-2xl font-bold mb-4">Create Recovery Plan</h2>

        <button
          className="btn btn-sm btn-circle absolute right-4 top-4"
          onClick={onClose}
          aria-label="Close"
        >
          ✕
        </button>

        {/* Plan name */}
        <div className="mb-5">
          <label className="block text-sm font-medium mb-1">Plan Name (unique)</label>
          <div className="flex gap-2 items-start">
            <input
              type="text"
              className="input input-bordered w-full max-w-lg"
              placeholder="e.g., default, starter_plan, week_1"
              value={planName}
              onChange={(e) => {
                setPlanName(e.target.value);
                setNameError(null);
                setStatus(null);
              }}
              onBlur={checkUniqueName}
            />
            <button
              className="btn"
              onClick={checkUniqueName}
              disabled={checkingName || !planName.trim()}
              title="Check name availability"
            >
              {checkingName ? "Checking..." : "Check"}
            </button>
          </div>
          {nameError && <p className="text-red-600 text-sm mt-1">{nameError}</p>}
        </div>

        {/* Days controls */}
        <div className="flex flex-wrap items-center gap-2 mb-6">
          <button className="btn btn-primary" onClick={addDay}>
            + Add Day
          </button>
          <button className="btn" onClick={clearAll}>
            Clear All
          </button>
          <span className="text-sm text-gray-600">{days.length} day{days.length === 1 ? "" : "s"}</span>
        </div>

        {/* Days list */}
        <div className="space-y-4">
          {days.map((day, dayIndex) => (
            <div
              key={`day-${dayIndex}`}
              className="rounded-2xl border border-gray-200 p-4"
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <h3 className="font-semibold">Day {dayIndex + 1}</h3>
                  <div className="flex gap-2">
                    <button
                      className="btn btn-sm"
                      onClick={() => moveDay(dayIndex, -1)}
                      disabled={dayIndex === 0}
                      title="Move up"
                    >
                      ↑
                    </button>
                    <button
                      className="btn btn-sm"
                      onClick={() => moveDay(dayIndex, +1)}
                      disabled={dayIndex === days.length - 1}
                      title="Move down"
                    >
                      ↓
                    </button>
                  </div>
                </div>

                <div className="flex gap-2">
                  <button
                    className="btn btn-sm"
                    onClick={() => addItem(dayIndex)}
                  >
                    + Add Item
                  </button>
                  <button
                    className="btn btn-sm btn-error"
                    onClick={() => removeDay(dayIndex)}
                  >
                    Remove Day
                  </button>
                </div>
              </div>

              {/* Items */}
              {day.items.length === 0 ? (
                <p className="text-sm text-gray-500">No items yet. Add one above.</p>
              ) : (
                <ul className="space-y-2">
                  {day.items.map((it, itemIndex) => (
                    <li
                      key={`day-${dayIndex}-item-${itemIndex}`}
                      className="flex flex-wrap items-center gap-3 rounded-xl border border-gray-200 p-3"
                    >
                      <span className="text-sm text-gray-500 w-16">
                        Item {itemIndex + 1}
                      </span>

                      <select
                        className="select select-bordered"
                        value={it.type}
                        onChange={(e) =>
                          changeItemType(dayIndex, itemIndex, e.target.value as ItemType)
                        }
                      >
                        {ITEM_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>

                      <button
                        className="btn btn-sm btn-error ml-auto"
                        onClick={() => removeItem(dayIndex, itemIndex)}
                      >
                        Remove
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>

        {/* Footer actions */}
        <div className="mt-6 flex flex-wrap items-center gap-3">
          <button
            className="btn btn-primary"
            onClick={savePlan}
            disabled={
              saving ||
              !planName.trim() ||
              !!nameError ||
              days.length === 0
            }
            title={!planName.trim() ? "Enter a unique plan name" : undefined}
          >
            {saving ? "Saving..." : "Save Plan"}
          </button>

          <button className="btn" onClick={onClose}>
            Cancel
          </button>

          {status && <span className="text-green-600 text-sm">{status}</span>}
          {error && <span className="text-red-600 text-sm">{error}</span>}
        </div>
      </div>
    </div>
  );
};

export default CreatePlan;